import * as Dialog from "@radix-ui/react-dialog";
import React from "react";
import { t } from "ttag";

import { DocumentChooserBlock, ImageChooserBlock } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Modal, ModalRoot } from "../Modal";

import styles from "./index.module.scss";

/**
 * In-page viewer for a menu supplied as page images, so it opens on every
 * device without a PDF plugin. Falls back to a link to the PDF itself when no
 * page images are set, and renders nothing when there is neither.
 */
export const MenuViewer = (props: {
    title: string;
    buttonText: string;
    buttonClassName: string;
    pages: ImageChooserBlock[];
    document: DocumentChooserBlock | null;
    /** Text version of the menu, offered to screen readers instead of the images. */
    textVersion?: string | null;
    /** Which microsite's colours the viewer takes. */
    palette?: "groups" | "happyHour";
}) => {
    if (props.pages.length === 0) {
        if (!props.document) {
            return null;
        }
        return (
            <a
                className={props.buttonClassName}
                href={props.document.url}
                target="_blank"
                rel="noreferrer"
            >
                {props.buttonText}
            </a>
        );
    }
    const count = props.pages.length;
    const title = props.title;
    return (
        <ModalRoot>
            <Dialog.Trigger asChild>
                <button type="button" className={props.buttonClassName}>
                    {props.buttonText}
                </button>
            </Dialog.Trigger>
            <Modal
                contentLabel={title}
                className={concatClassNames([
                    styles.dialog,
                    props.palette === "happyHour"
                        ? styles.happyHour
                        : styles.groups,
                ])}
                closeClassName={styles.dialogClose}
                overlayClassName={styles.dialogOverlay}
            >
                <div className={styles.bar}>
                    <p className={styles.title}>{title}</p>
                    {props.document && (
                        <a
                            className={styles.link}
                            href={props.document.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t`Open PDF`}
                        </a>
                    )}
                </div>
                <div className={styles.pages}>
                    {props.pages.map((page, index) => (
                        <img
                            key={`${page.url}-${index}`}
                            className={styles.page}
                            src={page.url}
                            alt={t`${title}, page ${index + 1} of ${count}`}
                            width={page.width}
                            height={page.height}
                            loading={index === 0 ? "eager" : "lazy"}
                        />
                    ))}
                    {props.textVersion && (
                        <section
                            className={styles.srOnly}
                            aria-label={t`${title}, text version`}
                        >
                            <RichTextBlock
                                className={concatClassNames([styles.srOnly])}
                                value={props.textVersion}
                            />
                        </section>
                    )}
                </div>
            </Modal>
        </ModalRoot>
    );
};
